import React from 'react';
import { Box, Typography, makeStyles, IconButton } from '@material-ui/core';
import { ChevronLeft, ChevronRight } from '@material-ui/icons';

type PaginationProps = {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  totalItems?: number;
  itemsPerPage?: number;
  className?: string;
};

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '5px',
  },
  paginationInfo: {
    fontSize: 12,
    fontWeight: 300,
    color: '#303030',
    lineHeight: '160%',
    fontFamily: 'Hiragino Kaku Gothic ProN',
    textAlign: 'left',
    width: '100%',
  },
  paginationWrapper: {
    display: 'flex',
    alignItems: 'center',
  },
  iconButton: {
    width: 50,
    height: 50,
    padding: 0,
    color: '#FF3B30',
    '&:disabled': {
      color: '#DCDCDC',
    },
  },
  pageButton: {
    width: 50,
    height: 'fix-content',
    padding: 0,
    minWidth: 'auto',
    color: '#DCDCDC',
    fontSize: 18,
    fontWeight: 600,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    '&:hover': {
      background: 'transparent',
    },
  },
  pageButtonActive: {
    color: '#FF3B30',
  },
  pageButtonBox: {
    borderRadius: '50%',
    border: '2px solid #DCDCDC',
    margin: '0 4px',
  },
  arrowIcon: {
    fontSize: 22,
  },
}));

export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  totalItems,
  itemsPerPage,
  className,
}) => {
  const classes = useStyles();

  const handlePrevious = (): void => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNext = (): void => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  const renderPageNumbers = (): JSX.Element[] => {
    const pages = [];
    const maxVisiblePages = 3;

    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is less than max visible
      for (let i = 1; i <= totalPages; i++) {
        const isActive = i === currentPage;
        pages.push(
          <button
            key={i}
            onClick={() => onPageChange(i)}
            className={`${classes.pageButton} ${!isActive ? classes.pageButtonActive : ''}`}
          >
            {i}
          </button>
        );
      }
    } else {
      // Show current, previous and next page
      let start = Math.max(1, currentPage - 1);
      const end = Math.min(totalPages, start + maxVisiblePages - 1);

      // Adjust start if we're near the end
      if (end - start < maxVisiblePages - 1) {
        start = Math.max(1, end - maxVisiblePages + 1);
      }

      for (let i = start; i <= end; i++) {
        const isActive = i === currentPage;
        pages.push(
          <button
            key={i}
            onClick={() => onPageChange(i)}
            className={`${classes.pageButton} ${!isActive ? classes.pageButtonActive : ''}`}
          >
            {i}
          </button>
        );
      }
    }

    return pages;
  };

  return (
    <Box className={`${classes.container} ${className}`}>
      <Typography className={classes.paginationInfo}>
        全{totalItems || 0}件（{currentPage}/{totalPages}ページ）
      </Typography>
      <Box className={classes.paginationWrapper}>
        <IconButton
          onClick={handlePrevious}
          disabled={currentPage === 1}
          className={classes.iconButton}
          aria-label="前のページ"
        >
          <ChevronLeft className={classes.arrowIcon} />
        </IconButton>

        {renderPageNumbers()}

        <IconButton
          onClick={handleNext}
          disabled={currentPage === totalPages}
          className={classes.iconButton}
          aria-label="次のページ"
        >
          <ChevronRight className={classes.arrowIcon} />
        </IconButton>
      </Box>
    </Box>
  );
};
